import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';
import HelpSidebar from '../components/HelpSidebar';
import Modal from '../components/Modal';
import '../styles/editor.css';

// ─── Types ───────────────────────────────────────────────────────────

interface Version {
  versionId: string;
  createdAt: string;
  contentCount: number;
  snapshotSizeBytes: number;
  status: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' at ' + date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
}

// ─── Component ───────────────────────────────────────────────────────

export default function VersionHistory() {
  const { siteId } = useParams<{ siteId: string }>();
  const { addToast } = useToast();

  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [restoreModal, setRestoreModal] = useState<Version | null>(null);
  const [restoring, setRestoring] = useState(false);

  const fetchVersions = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.listVersions(siteId);
      setVersions(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchVersions();
  }, [fetchVersions]);

  const handleRestore = async () => {
    if (!siteId || !restoreModal) return;
    setRestoring(true);
    try {
      const result = await api.restoreVersion(siteId, restoreModal.versionId);
      addToast('success', `Restored ${result.restoredCount} content items. Publish your site to make the changes live.`);
      setRestoreModal(null);
      fetchVersions();
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to restore version');
    } finally {
      setRestoring(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Version History</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="page-header"><h1>Version History</h1></div>
        <div className="error-state"><p>{error}</p></div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <Link to={`/sites/${siteId}`} className="back-link">← Back to Dashboard</Link>
          <h1>Version History</h1>
        </div>
      </div>

      <div className="editor-layout">
        <div className="editor-main">
          {versions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><svg width={48} height={48} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" style={{ color: '#ccc' }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div>
              <h2>No versions yet</h2>
              <p>Versions are created automatically each time you publish.</p>
              <Link to={`/sites/${siteId}/publish`} className="btn btn-primary">
                Go to Publish
              </Link>
            </div>
          ) : (
            <div className="list-items">
              {versions.map((version, index) => (
                <div key={version.versionId} className="list-item">
                  <div className="list-item-content">
                    <h3 className="list-item-title">
                      {formatDate(version.createdAt)}
                      {index === 0 && (
                        <span className="tag tag-accent" style={{ marginLeft: 8 }}>Current</span>
                      )}
                    </h3>
                    <div className="list-item-meta">
                      <span>{version.contentCount} content items</span>
                      <span style={{ margin: '0 8px', opacity: 0.4 }}>·</span>
                      <span>{formatSize(version.snapshotSizeBytes)}</span>
                    </div>
                  </div>
                  <div className="list-item-actions">
                    {index > 0 && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setRestoreModal(version)}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <HelpSidebar
          title="Version History"
          description="Every time you publish, a snapshot of your content is saved. You can restore any previous version if needed."
          tips={[
            'Versions are created automatically when you publish',
            'Restoring a version replaces all your current content',
            'After restoring, publish your site to make the changes live',
            'Old versions are automatically cleaned up when storage is full',
          ]}
        />
      </div>

      {/* Restore Confirmation Modal */}
      <Modal
        open={restoreModal !== null}
        onClose={() => setRestoreModal(null)}
        title="Restore Version"
        actions={
          <>
            <button className="btn btn-secondary" onClick={() => setRestoreModal(null)}>
              Cancel
            </button>
            <button className="btn btn-danger" onClick={handleRestore} disabled={restoring}>
              {restoring ? 'Restoring…' : 'Restore'}
            </button>
          </>
        }
      >
        {restoreModal && (
          <p>
            Restore to this version? This will replace all current content with the snapshot
            from <strong>{formatDate(restoreModal.createdAt)}</strong>. You should publish again
            after restoring.
          </p>
        )}
      </Modal>
    </div>
  );
}
